/**
 * The Edit Room modal — shared by the room list and the room page.
 *
 * It lived inline in project-detail's pencil-button handler until 2026-09-18,
 * when Mick asked to edit a room from inside it too. Moved here rather than
 * copied, so the two entry points cannot drift: a copy is how the room page
 * would quietly keep a Status dropdown the list had deliberately dropped.
 */

import { openModal, type FieldDef } from '@/lib/modal';
import { updateRoom, deleteRoom } from '@/lib/data/rooms';
import type { RoomRow } from '@/types/database';
import { emptyToNull } from '@/lib/form-values';

type EditableRoom = Pick<RoomRow, 'id' | 'project_id' | 'name' | 'is_exterior' | 'notes'>;

export function roomEditFields(room: EditableRoom): FieldDef[] {
  return [
    { name: 'name', label: 'Name', required: true, defaultValue: room.name },
    {
      // Where a misfiled room gets moved (chosen 2026-09-18). Without this,
      // fixing one meant deleting and re-adding it, losing its materials and
      // photos.
      name: 'location', label: 'Location', type: 'select',
      options: [
        { value: 'interior', label: 'Interior' },
        { value: 'exterior', label: 'Exterior' },
      ],
      defaultValue: room.is_exterior ? 'exterior' : 'interior',
    },
    // No Status here. A free dropdown over every status would let a room jump
    // Proposed → Completed — skipping the readiness checklist, and refused by
    // the database anyway. Status changes through the room page's status
    // buttons only, where the transitions are right.
    { name: 'notes', label: 'Notes', type: 'textarea', voice: true, defaultValue: room.notes },
  ];
}

export function roomEditPatch(
  values: Record<string, string | boolean>,
): Pick<RoomRow, 'name' | 'is_exterior' | 'notes'> {
  return {
    name: String(values.name ?? ''),
    is_exterior: values.location === 'exterior',
    notes: emptyToNull(values.notes),
  };
}

/**
 * Where to go once a room is deleted from its own page — the list it lived on.
 * Interior is section key `rooms` (stable URL key, per CLAUDE.md), not
 * `interior`.
 */
export function roomListHash(room: Pick<RoomRow, 'project_id' | 'is_exterior'>): string {
  return `#/projects/${room.project_id}/${room.is_exterior ? 'exterior' : 'rooms'}`;
}

export function openEditRoomModal(
  room: EditableRoom,
  opts: { onSaved: () => void; onDeleted: () => void },
): void {
  openModal({
    title: 'Edit room',
    showDelete: true,
    fields: roomEditFields(room),
    onSubmit: async (v) => {
      await updateRoom(room.id, roomEditPatch(v));
      opts.onSaved();
    },
    onDelete: async () => {
      await deleteRoom(room.id);
      opts.onDeleted();
    },
  });
}
